/**
 * FIT REPORT PDF 전용 뷰.
 *
 * 일반 상세 화면(FitReportDetail)과 분리된 전용 페이지로,
 * Playwright headless Chromium이 이 페이지를 A4(190mm × 277mm 사용 가능)로 렌더링하여 PDF 생성.
 *
 * 핵심:
 * - 각 A4 페이지는 정확히 한 화면 컴포넌트(.pdf-page)로 구성
 * - 컴포넌트 내부는 mm 기반 절대 사이즈로 고정 → 컨텐츠 넘침 방지
 * - 동일 디자인 시스템(Recharts/오렌지 포인트/orange-50)을 그대로 사용 → 유지보수성 확보
 * - 인쇄 시 `page-break-after: always`로 페이지 분리
 *
 * 라우트: `/admin/fit-reports/:reportId/pdf-view`
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import apiClient from '../../api/client';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend,
} from 'recharts';

const AREAS = [
  { key: 'strength',           score_field: 'strength_score',          label: '근력' },
  { key: 'muscular_endurance', score_field: 'endurance_score',         label: '근지구력' },
  { key: 'cardio',             score_field: 'cardio_score',            label: '심폐지구력' },
  { key: 'balance',            score_field: 'body_balance_score',      label: '체형' },
  { key: 'body_composition',   score_field: 'body_composition_score',  label: '체성분' },
];

const fitLevel = (score) => {
  if (score === null || score === undefined || score === '' || Number.isNaN(Number(score))) return null;
  const v = Number(score);
  if (v < 30) return 'Level 0';
  if (v < 60) return 'Level 1';
  if (v < 80) return 'Level 2';
  if (v < 95) return 'Level 3';
  return 'Level 4';
};

const LEVEL_SHORT = { 'Level 0': 'L0', 'Level 1': 'L1', 'Level 2': 'L2', 'Level 3': 'L3', 'Level 4': 'L4' };

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s.length === 10 ? s + 'T00:00:00' : s);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
};

export default function FitReportPdfView() {
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiClient.get(`/admin/fit-reports/${reportId}`)
      .then((r) => setReport(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || '로드 실패'));
  }, [reportId]);

  const score = report?.score_snapshot || {};
  const comparison = report?.comparison_snapshot || {};
  const roadmap = report?.roadmap_snapshot || {};
  const m = report?.member_snapshot || {};

  const p1Data = useMemo(() => AREAS.map((a) => ({
    subject: a.label,
    value: Number(score?.[a.score_field] ?? 0) || 0,
  })), [score]);

  const p2Data = useMemo(() => AREAS.map((a) => ({
    subject: a.label,
    나: Number(score?.[a.score_field] ?? 0) || 0,
    평균: Number(comparison?.averages?.[a.key] ?? 0) || 0,
  })), [score, comparison]);

  const p3Data = useMemo(() => AREAS.map((a) => {
    const it = (roadmap?.items || []).find((x) => x.key === a.key) || {};
    return {
      subject: a.label,
      현재: Number(score?.[a.score_field] ?? 0) || 0,
      '3개월 목표': Number(it.three_month ?? 0) || 0,
    };
  }), [score, roadmap]);

  if (err) return <div style={{ padding: 20, color: 'crimson' }}>{err}</div>;
  if (!report) return <div data-testid="pdf-loading" style={{ padding: 20 }}>Loading...</div>;

  // Playwright가 렌더링 완료를 감지하는 신호
  const ready = AREAS.every((a) => score?.[a.score_field] !== undefined);

  return (
    <>
      {/* A4 사용 가능 영역 = 210mm × 297mm, 마진 10mm 양쪽 → 190mm × 277mm */}
      <style>{`
        :root { color-scheme: light; }
        html, body, #root { margin: 0; padding: 0; background: white; }
        body { font-family: -apple-system, 'Pretendard', 'Noto Sans KR', 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif; color: #1F2937; }
        /* 리포트 PDF 출력 시 플랫폼 뱃지 숨김 (CRM 화면에서는 그대로 표시됨) */
        #emergent-badge { display: none !important; }
        .pdf-page {
          width: 190mm;
          height: 277mm;
          padding: 6mm 8mm;
          margin: 0 auto;
          box-sizing: border-box;
          page-break-after: always;
          break-after: page;
          page-break-inside: avoid;
          break-inside: avoid;
          overflow: hidden;
          position: relative;
          background: white;
        }
        .pdf-page:last-child { page-break-after: auto; break-after: auto; }
        .pdf-header { display: flex; align-items: flex-end; justify-content: space-between; padding-bottom: 3mm; border-bottom: 1px solid #F3F4F6; }
        .pdf-brand { letter-spacing: 0.3em; color: #F97316; font-weight: 700; font-size: 9pt; }
        .pdf-title { font-size: 18pt; font-weight: 800; margin-top: 1mm; color: #111827; }
        .pdf-subtitle { font-size: 10pt; color: #6B7280; margin-top: 0.5mm; }
        .pdf-pageno { font-size: 9pt; color: #9CA3AF; font-weight: 600; }
        .pdf-member { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4mm; margin-top: 4mm; }
        .pdf-member .lbl { font-size: 8pt; color: #9CA3AF; }
        .pdf-member .val { font-size: 11pt; font-weight: 600; color: #111827; margin-top: 0.5mm; }
        .pdf-section-title { font-size: 9pt; font-weight: 700; color: #4B5563; letter-spacing: 0.05em; margin-bottom: 2mm; margin-top: 4mm; }
        .pdf-fit-score { text-align: center; margin-top: 3mm; }
        .pdf-fit-score .label { color: #F97316; letter-spacing: 0.3em; font-size: 9pt; font-weight: 700; }
        .pdf-fit-score .value { font-size: 48pt; font-weight: 800; color: #111827; line-height: 1; margin-top: 1mm; }
        .pdf-fit-score .value .unit { font-size: 14pt; color: #9CA3AF; font-weight: 500; margin-left: 2mm; }
        .pdf-fit-score .desc { font-size: 9pt; color: #6B7280; margin-top: 2mm; max-width: 130mm; margin-left: auto; margin-right: auto; line-height: 1.4; }
        .pdf-radar-wrap { width: 100%; display: flex; justify-content: center; align-items: center; }
        .pdf-area-cards { display: grid; grid-template-columns: repeat(5, 1fr); gap: 2mm; margin-top: 2mm; }
        .pdf-area-card { border: 1px solid #FED7AA; background: linear-gradient(180deg,#FFF7ED40 0%,#fff 100%); border-radius: 2mm; padding: 2mm; text-align: center; }
        .pdf-area-card .lbl { font-size: 8pt; color: #6B7280; font-weight: 500; }
        .pdf-area-card .level-box { display: inline-flex; flex-direction: column; align-items: center; justify-content: center; padding: 1mm 2mm; border-radius: 2mm; border: 1.5px solid; margin-top: 1.5mm; }
        .pdf-area-card .level-box.has { border-color: #FDBA74; background: #FFEDD5; color: #C2410C; }
        .pdf-area-card .level-box.empty { border-color: #E5E7EB; background: #F9FAFB; color: #9CA3AF; }
        .pdf-area-card .level-box .pre { font-size: 7pt; font-weight: 700; letter-spacing: 0.1em; opacity: 0.85; }
        .pdf-area-card .level-box .num { font-size: 18pt; font-weight: 800; line-height: 1; margin-top: 0.5mm; }
        .pdf-area-card .score { font-size: 8pt; color: #6B7280; margin-top: 1.5mm; }
        .pdf-area-card .score b { color: #374151; font-weight: 700; }
        .pdf-guide { margin-top: 3mm; border: 1px solid #E5E7EB; background: #F9FAFB; border-radius: 2mm; padding: 2mm 3mm; }
        .pdf-guide .gtitle { font-size: 8pt; color: #4B5563; font-weight: 700; letter-spacing: 0.05em; }
        .pdf-guide .grow { display: grid; grid-template-columns: repeat(5, 1fr); gap: 1.5mm; margin-top: 1.5mm; }
        .pdf-guide .gbox { background: white; border: 1px solid #F3F4F6; border-radius: 1.5mm; padding: 1.5mm; text-align: center; font-size: 8pt; }
        .pdf-guide .gbox .l { color: #374151; font-weight: 700; }
        .pdf-guide .gbox .d { color: #6B7280; margin-top: 0.5mm; }
        .pdf-guide .gnote { font-size: 7.5pt; color: #6B7280; margin-top: 1.5mm; line-height: 1.4; }

        /* Tables */
        .pdf-table { width: 100%; border-collapse: collapse; border: 1px solid #E5E7EB; border-radius: 2mm; overflow: hidden; margin-top: 3mm; }
        .pdf-table thead th { background: #F9FAFB; color: #4B5563; font-size: 8.5pt; font-weight: 600; padding: 1.5mm 3mm; text-align: left; }
        .pdf-table td { padding: 1.5mm 3mm; font-size: 9pt; border-top: 1px solid #F3F4F6; }
        .pdf-table td.num { text-align: right; tabular-nums: true; }
        .pdf-table td.ctr { text-align: center; }
        .pdf-diff-pos { color: #059669; font-weight: 700; }
        .pdf-diff-neg { color: #EF4444; font-weight: 700; }
        .pdf-table-title { display: flex; align-items: center; gap: 1.5mm; font-size: 8pt; font-weight: 700; color: #6B7280; background: #F9FAFB; padding: 1.5mm 3mm; border: 1px solid #E5E7EB; border-bottom: 0; border-top-left-radius: 2mm; border-top-right-radius: 2mm; }
        .pdf-table.attached { border-top-left-radius: 0; border-top-right-radius: 0; margin-top: 0; }

        /* Top 2 */
        .pdf-top2-row { display: grid; grid-template-columns: 1fr 1fr; gap: 3mm; margin-top: 3mm; }
        .pdf-top2 { border-radius: 2mm; padding: 3mm; }
        .pdf-top2.strength { background: #ECFDF54D; border: 1px solid #A7F3D0; }
        .pdf-top2.priority { background: #FFF7ED4D; border: 1px solid #FED7AA; }
        .pdf-top2 .head { display: flex; gap: 2mm; align-items: center; font-weight: 700; }
        .pdf-top2.strength .head { color: #065F46; }
        .pdf-top2.priority .head { color: #9A3412; }
        .pdf-top2 .item { background: white; border: 1px solid; border-radius: 1.5mm; padding: 1.5mm 3mm; margin-top: 1.5mm; font-size: 10pt; font-weight: 500; color: #1F2937; }
        .pdf-top2.strength .item { border-color: #A7F3D0; }
        .pdf-top2.priority .item { border-color: #FED7AA; }
        .pdf-top2 .empty { font-size: 9pt; color: #9CA3AF; margin-top: 1.5mm; }

        /* Empty state */
        .pdf-empty { border: 1px dashed #E5E7EB; padding: 12mm; text-align: center; color: #9CA3AF; border-radius: 2mm; margin-top: 6mm; font-size: 10pt; }

        /* Roadmap notice */
        .pdf-notice { font-size: 9pt; color: #6B7280; line-height: 1.5; margin-top: 2mm; }
      `}</style>

      {/* Marker: ready indicator for Playwright */}
      <div id="pdf-ready" data-ready={ready ? '1' : '0'} style={{ display: 'none' }} />

      {/* ============ PAGE 1: 현재 나의 체력 상태 ============ */}
      <div className="pdf-page" data-testid="pdf-page-1">
        <div className="pdf-header">
          <div>
            <div className="pdf-brand">INSIGHT FIT</div>
            <div className="pdf-title">FIT REPORT</div>
            <div className="pdf-subtitle">현재 나의 체력 상태</div>
          </div>
          <div className="pdf-pageno">PAGE 1 / 3</div>
        </div>

        <div className="pdf-member">
          <div><div className="lbl">회원명</div><div className="val">{m.name || '-'}</div></div>
          <div><div className="lbl">성별 · 나이</div><div className="val">{m.gender === 'male' ? '남' : m.gender === 'female' ? '여' : '-'} · {m.age ?? '-'}세</div></div>
          <div><div className="lbl">전화번호</div><div className="val">{m.phone || '-'}</div></div>
          <div><div className="lbl">Report Date</div><div className="val">{fmtDate(report.report_date)}</div></div>
        </div>

        <div className="pdf-fit-score">
          <div className="label">FIT SCORE</div>
          <div className="value">{score?.total_score ?? '-'}<span className="unit">점</span></div>
          <div className="desc">
            INSIGHT FIT는 종합 등급이 아닌 <b>5가지 영역의 균형</b>을 중요시합니다.
            아래 항목별 점수와 Level을 확인하세요.
          </div>
        </div>

        <div className="pdf-radar-wrap" style={{ height: '95mm', marginTop: '2mm' }}>
          <div style={{ width: '140mm', height: '95mm' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={p1Data} outerRadius="80%">
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#1F2937', fontSize: 11, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 7, fill: '#9CA3AF' }} />
                <Radar name="현재" dataKey="value" stroke="#F97316" fill="#F97316" fillOpacity={0.45} strokeWidth={2} isAnimationActive={false} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="pdf-section-title">항목별 점수</div>
        <div className="pdf-area-cards">
          {AREAS.map((a) => {
            const v = score?.[a.score_field];
            const lvl = fitLevel(v);
            return (
              <div key={a.key} className="pdf-area-card">
                <div className="lbl">{a.label}</div>
                <div className={'level-box ' + (lvl ? 'has' : 'empty')}>
                  <span className="pre">{lvl ? 'LEVEL' : ''}</span>
                  <span className="num">{lvl ? lvl.replace('Level ', '') : '미측정'}</span>
                </div>
                <div className="score"><b>{v ?? '-'}</b>{(v !== null && v !== undefined && v !== '') ? '점' : ''}</div>
              </div>
            );
          })}
        </div>

        <div className="pdf-guide">
          <div className="gtitle">LEVEL GUIDE</div>
          <div className="grow">
            {[
              ['Level 4','상위 5%'],['Level 3','상위 20%'],['Level 2','평균 이상'],['Level 1','평균'],['Level 0','하위 40%'],
            ].map(([l,d]) => (
              <div key={l} className="gbox"><div className="l">{l}</div><div className="d">{d}</div></div>
            ))}
          </div>
          <div className="gnote">Level은 0~4까지 총 5단계로 구성됩니다. 회원님의 최근 측정 데이터를 기반으로 자동 계산됩니다.</div>
        </div>
      </div>

      {/* ============ PAGE 2: 평균 비교 ============ */}
      <div className="pdf-page" data-testid="pdf-page-2">
        <div className="pdf-header">
          <div>
            <div className="pdf-brand">INSIGHT FIT</div>
            <div className="pdf-title">나는 평균보다 어떤가?</div>
            <div className="pdf-subtitle">동일 성별·연령 평균 대비 나의 위치</div>
          </div>
          <div className="pdf-pageno">PAGE 2 / 3</div>
        </div>

        {!comparison?.has_data ? (
          <div className="pdf-empty">평균 비교 데이터가 부족합니다.<br /><span style={{ fontSize: '8pt' }}>기준표 관리에서 연령 평균값을 먼저 설정해주세요.</span></div>
        ) : (
          <>
            <div className="pdf-radar-wrap" style={{ height: '115mm', marginTop: '3mm' }}>
              <div style={{ width: '155mm', height: '115mm' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={p2Data} outerRadius="78%">
                    <PolarGrid stroke="#E5E7EB" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#1F2937', fontSize: 11, fontWeight: 700 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 7, fill: '#9CA3AF' }} />
                    <Radar name="나" dataKey="나" stroke="#F97316" fill="#F97316" fillOpacity={0.45} strokeWidth={2} isAnimationActive={false} />
                    <Radar name="평균" dataKey="평균" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.2} strokeWidth={1.5} strokeDasharray="5 3" isAnimationActive={false} />
                    <Legend wrapperStyle={{ fontSize: '9pt' }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <table className="pdf-table">
              <thead><tr><th>항목</th><th style={{ textAlign: 'right' }}>나</th><th style={{ textAlign: 'right' }}>평균</th><th style={{ textAlign: 'right' }}>차이</th></tr></thead>
              <tbody>
                {(comparison.items || []).map((it) => (
                  <tr key={it.key}>
                    <td>{it.label}</td>
                    <td className="num">{it.my ?? '-'}</td>
                    <td className="num">{it.average ?? '-'}</td>
                    <td className="num">
                      {it.diff === null || it.diff === undefined ? '-' :
                        it.diff > 0 ? <span className="pdf-diff-pos">+{it.diff}</span>
                        : it.diff < 0 ? <span className="pdf-diff-neg">{it.diff}</span>
                        : <span>0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pdf-top2-row">
              <div className="pdf-top2 strength">
                <div className="head">🏆 강점 TOP 2</div>
                {(comparison.top_strengths || []).length > 0
                  ? comparison.top_strengths.map((s, i) => (
                      <div key={s.key} className="item">{i === 0 ? '🥇' : '🥈'} {s.label}</div>
                    ))
                  : <div className="empty">평균 비교 데이터가 부족합니다.</div>}
              </div>
              <div className="pdf-top2 priority">
                <div className="head">🎯 성장 우선순위 TOP 2</div>
                {(comparison.top_priorities || []).length > 0
                  ? comparison.top_priorities.map((s, i) => (
                      <div key={s.key} className="item">{i === 0 ? '①' : '②'} {s.label}</div>
                    ))
                  : <div className="empty">평균 비교 데이터가 부족합니다.</div>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ============ PAGE 3: 성장 로드맵 ============ */}
      <div className="pdf-page" data-testid="pdf-page-3">
        <div className="pdf-header">
          <div>
            <div className="pdf-brand">INSIGHT FIT</div>
            <div className="pdf-title">FIT LEVEL ROADMAP</div>
            <div className="pdf-subtitle">성장 로드맵</div>
          </div>
          <div className="pdf-pageno">PAGE 3 / 3</div>
        </div>

        <div className="pdf-notice">
          현재 위치를 확인했습니다. 이제 다음 성장 목표를 확인해보세요. <br />
          <span style={{ color: '#9CA3AF' }}>아래 목표 영역은 INSIGHT FIT 회원 평균 성장 데이터를 기반으로 계산되었습니다.</span>
        </div>

        <div className="pdf-radar-wrap" style={{ height: '95mm', marginTop: '2mm' }}>
          <div style={{ width: '140mm', height: '95mm' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={p3Data} outerRadius="78%">
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: '#1F2937', fontSize: 11, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 7, fill: '#9CA3AF' }} />
                <Radar name="현재" dataKey="현재" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.22} strokeWidth={1.5} isAnimationActive={false} />
                <Radar name="3개월 목표" dataKey="3개월 목표" stroke="#F97316" fill="#F97316" fillOpacity={0.45} strokeWidth={2} isAnimationActive={false} />
                <Legend wrapperStyle={{ fontSize: '9pt' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="pdf-table-title">성장 목표 (점수)</div>
        <table className="pdf-table attached">
          <thead><tr><th>항목</th><th style={{ textAlign: 'right' }}>현재</th><th style={{ textAlign: 'right' }}>3개월</th><th style={{ textAlign: 'right' }}>6개월</th></tr></thead>
          <tbody>
            {(roadmap.items || []).map((it) => (
              <tr key={it.key}>
                <td>{it.label}</td>
                <td className="num">{it.current ?? '-'}</td>
                <td className="num" style={{ color: '#EA580C', fontWeight: 600 }}>{it.three_month ?? '-'}</td>
                <td className="num" style={{ color: '#C2410C', fontWeight: 700 }}>{it.six_month ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="pdf-table-title" style={{ marginTop: '3mm' }}>Level 변화</div>
        <table className="pdf-table attached">
          <thead><tr><th>항목</th><th style={{ textAlign: 'center' }}>현재</th><th style={{ textAlign: 'center' }}>3개월</th><th style={{ textAlign: 'center' }}>6개월</th></tr></thead>
          <tbody>
            {(roadmap.items || []).map((it) => (
              <tr key={it.key}>
                <td>{it.label}</td>
                <td className="ctr">{LEVEL_SHORT[it.current_level] || '-'}</td>
                <td className="ctr" style={{ color: '#EA580C', fontWeight: 600 }}>{LEVEL_SHORT[it.three_month_level] || '-'}</td>
                <td className="ctr" style={{ color: '#C2410C', fontWeight: 700 }}>{LEVEL_SHORT[it.six_month_level] || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
